import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../components/Layout/Layout.component';
import { SelectableTable } from '../../components/SelectableTable/SelectableTable.component';
import {
  ForceSelectionCallback,
  SelectableTableColumn,
  TableSelection,
} from '../../components/SelectableTable/SelectableTable.types';
import ViewContainer from '../../components/ViewContainer/ViewContainer.component';
import { useStyles } from './AnnotationsAssignment.styles';
import {
  ExamSheetStructureWithAnnotations,
  IAnnotationAssigment,
  ExamTaskCriterion,
  ExamTaskCriterionsTableItem,
  ExamTasksGroup,
  ExamSheetAnnotations,
  ExamTask,
} from './AnnotationsAssignment.types';
import { Typography } from '@material-ui/core';
import { SelectedRecordCountDisplay } from '../../components/SelectableTable/SelectedRecordCountDisplay/SelectedRecordCountDisplay.component';
import AssignAdnotationsButton from './AnnotationsAssignButton.component.tsx/AssignAnnotationsButton';
import CalculationAnnotationIcon from '../../assets/icons/calculation-annotation.svg';
import GradingAnnotationIcon from '../../assets/icons/grading-annotation.svg';
import TooltipOne from '../../components/TooltipOne/TooltipOne.component';
import {
  getExamSheetAnnotations,
  getExamSheetStructureWithAnnotations,
} from '../../services/exams/examSheet/examSheet.service';
import { getAnnotationDefinitions } from '../../services/admin/settings/annotations.service';
import { AnnotationDefinition } from '../../models/annotation.types';
import { Prompt } from '../../components/Prompt/Prompt.component';
import { AxiosResponse } from 'axios';
import AnnotationAssigmentEditor from './AnnotationAssigmentEditor.component';
import { useLayoutContext } from '../../Context/Layout.context';
import useDocumentTitle from '../../hooks/DocumentTitle/documentTitle';
import { useTranslation } from 'react-i18next';

interface ExamTasksGroupsCriterionsIds {
  groupId: string;
  inferiors: string[];
}
interface ExamTasksCriterionsIds {
  groupId: string;
  taskId: string;
  inferiors: string[];
}

interface AnnotationsProps {
  sheetId: string;
}

const indentation = 15;

const Annotations: React.FC<AnnotationsProps> = ({ sheetId }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { openAlert } = useLayoutContext();

  const [examTasksGroups, setExamTasksGroups] = useState<ExamTasksGroup[]>([]);
  const [assignedAnnotations, setAssignedAnnotations] = useState<IAnnotationAssigment[]>([]);
  const [transformedExamTasksGroupsStructure, setTransformedExamTasksGroupsStructure] = useState<ExamTaskCriterion[]>(
    [],
  );
  const [itemsSelection, setItemsSelection] = useState<TableSelection>({ items: [] });
  const [onForceSelectionCallback, setOnForceSelectionCallback] = useState<ForceSelectionCallback | undefined>(
    undefined,
  );

  const [examTaskCriterionsTableData, setExamTaskCriterionsTableData] = useState<ExamTaskCriterionsTableItem[]>([]);
  const [existingAnnotationInfoModalOpen, setExistingAnnotationInfoModalOpen] = useState<boolean>(false);

  const [examTasksGroupsCriterionsIds, setExamTasksGroupsCriterionsIds] = useState<ExamTasksGroupsCriterionsIds[]>([]);
  const [examTasksGroupsIds, setExamTasksGroupsIds] = useState<string[]>([]);
  const [examTasksCriterionsIds, setExamTasksCriterionsIds] = useState<ExamTasksCriterionsIds[]>([]);

  const [isAnnotationsTableOpen, setIsAnnotationsTableOpen] = useState<boolean>(false);
  const [isSelectionDisabled, setIsSelectionDisabled] = useState<boolean>(false);
  const [sessionNameElement, setSessionNameElement] = useState<string>('');
  const [annotationDefinitions, setAnnotationDefinitions] = useState<AnnotationDefinition[]>([]);
  const [taskIds, setTaskIds] = useState<string[]>([]);

  useDocumentTitle('Adnotacje');

  const columnsTaskCriterionTable: SelectableTableColumn[] = [
    {
      id: 'id',
      type: 'checkbox',
      title: '',
      filterType: null,
    },
    {
      id: 'label',
      type: 'text',
      title: 'Etykieta (GZ, Zad. kryt.)',
      filterType: null,
    },
    {
      id: 'name',
      type: 'text',
      title: 'Nazwa kryterium',
      filterType: null,
    },
  ];

  if (!isAnnotationsTableOpen) {
    columnsTaskCriterionTable.push({
      id: 'annotations',
      type: 'children',
      title: '',
      filterType: null,
    });
  }

  useEffect(() => {
    getAnnotationDefinitions().then(
      (annotationDefinitions: AnnotationDefinition[]) => {
        setAnnotationDefinitions(annotationDefinitions);
      },
      () => openAlert('get_error'),
    );
  }, []);

  const getAnnotationsExamSheetStructureAssignment = () => {
    getExamSheetStructureWithAnnotations(sheetId).then(
      (res: AxiosResponse<ExamSheetStructureWithAnnotations>) => {
        setExamTasksGroups(res.data.examTaskGroups);
        setSessionNameElement(
          `${res.data.dashboardSessionName} / ${res.data.dashboardExamName} / ${res.data.dashboardSheetCode}`,
        );
      },
      () => openAlert('get_error'),
    );
  };

  const openEditor = () => {
    setIsAnnotationsTableOpen(true);
    setIsSelectionDisabled(true);
  };

  const getAssignedAnnotationsAndOpenEditor = (id?: string) => {
    const selectedIds = id
      ? [id]
      : itemsSelection.items.filter((e) => !examTasksGroupsIds.includes(e)).filter((i) => !taskIds.includes(i));

    onForceSelectionCallback && onForceSelectionCallback({ items: selectedIds });

    getExamSheetAnnotations(selectedIds[0]).then(
      (res: AxiosResponse<ExamSheetAnnotations>) => {
        setAssignedAnnotations(res.data.annotations);
        if (
          !id &&
          selectedIds.find((criterionId: string) => {
            return examTasksGroups.find((taskGroup: ExamTasksGroup) => {
              return taskGroup.examTasks.find((task: ExamTask) => {
                return task.examTaskCriterions.find((criterion: ExamTaskCriterion) => {
                  return (
                    criterion.id === criterionId && (criterion.calculatingAnnotation || criterion.gradingAnnotation)
                  );
                });
              });
            });
          })
        ) {
          setExistingAnnotationInfoModalOpen(true);
        } else {
          openEditor();
        }
      },
      () => openAlert('get_error'),
    );
  };

  useEffect(() => {
    getAnnotationsExamSheetStructureAssignment();
  }, [sheetId]);

  useEffect(() => {
    if (examTasksGroups && examTasksGroups.length > 0) {
      const data: ExamTaskCriterion[] = [];
      examTasksGroups.map((item) => {
        data.push({
          id: item.name,
          criterionLabel: item.name,
          criterionName: '',
          indent: 1,
          calculatingAnnotation: false,
          gradingAnnotation: false,
        });
        item.examTasks.map((item) => {
          data.push({
            id: item.name,
            criterionLabel: `Zadanie ${item.name}`,
            criterionName: '',
            indent: 2,
            calculatingAnnotation: false,
            gradingAnnotation: false,
          });
          item.examTaskCriterions.map((item) => {
            data.push({
              id: item.id,
              criterionLabel: `Kryterium ${item.criterionLabel}`,
              criterionName: item.criterionName,
              indent: 3,
              calculatingAnnotation: item.calculatingAnnotation,
              gradingAnnotation: item.gradingAnnotation,
            });
          });
        });
      });
      setTransformedExamTasksGroupsStructure(data);
    }
  }, [examTasksGroups]);

  useEffect(() => {
    if (examTasksGroups && examTasksGroups.length > 0) {
      const examTasksGroupsIds: string[] = [];
      const examTasksGroupsCriterionsIds: ExamTasksGroupsCriterionsIds[] = [];
      const examTasksCriterionsIds: ExamTasksCriterionsIds[] = [];
      examTasksGroups.forEach((item, index) => {
        examTasksGroupsIds.push(item.name);
        examTasksGroupsCriterionsIds.push({ groupId: item.name, inferiors: [] });
        item.examTasks.forEach((task) => {
          examTasksCriterionsIds.push({
            groupId: item.name,
            taskId: task.name,
            inferiors: task.examTaskCriterions.map((e) => e.id),
          });
          task.examTaskCriterions.map((item) => {
            examTasksGroupsCriterionsIds[index].inferiors.push(item.id);
          });
        });
      });
      setExamTasksGroupsIds(examTasksGroupsIds);
      setExamTasksGroupsCriterionsIds(examTasksGroupsCriterionsIds);
      setExamTasksCriterionsIds(examTasksCriterionsIds);
      setTaskIds(examTasksCriterionsIds.map((i) => i.taskId));
    }
  }, [examTasksGroups]);

  useEffect(() => {
    const examTaskCriterionsTableData: ExamTaskCriterionsTableItem[] = transformedExamTasksGroupsStructure.map(
      (data: ExamTaskCriterion) => ({
        id: data.id,
        label: (
          <div
            style={{
              fontWeight: data.indent === 1 || data.indent === 2 ? 700 : '',
              color: data.indent === 1 ? '#3C419A' : '',
              marginLeft: data.indent ? data.indent * indentation + 'px' : '',
            }}
          >
            {data.criterionLabel}
          </div>
        ),
        name: data.criterionName,
        annotations: (
          <div className={classes.annotationsTableCell}>
            <TooltipOne title="Adnotacje do obliczeń" placement="top">
              <div>
                {data.calculatingAnnotation && (
                  <img
                    className={classes.annotationIcon}
                    src={CalculationAnnotationIcon}
                    onClick={() => getAssignedAnnotationsAndOpenEditor(data.id)}
                    alt="ikona adnotacja do obliczeń"
                  />
                )}
              </div>
            </TooltipOne>
            <TooltipOne title="Adnotacje do oceniania" placement="top">
              <div>
                {data.gradingAnnotation && (
                  <img
                    className={classes.annotationIcon}
                    src={GradingAnnotationIcon}
                    onClick={() => getAssignedAnnotationsAndOpenEditor(data.id)}
                    alt="ikona adnotacja do oceniania"
                  />
                )}
              </div>
            </TooltipOne>
          </div>
        ),
      }),
    );
    setExamTaskCriterionsTableData(examTaskCriterionsTableData);
  }, [transformedExamTasksGroupsStructure]);

  const handleItemSelectionChange = useCallback(
    (itemId: string) => {
      let newSelection: string[] = [];
      if (!onForceSelectionCallback) {
        return;
      }
      const alreadySelected = !!itemsSelection.items.find((s) => s === itemId);
      const selectedGroup = examTasksGroupsCriterionsIds.find((s) => s.groupId === itemId);
      const selectedTask = examTasksCriterionsIds.find((s) => s.taskId === itemId);

      if (selectedGroup) {
        if (alreadySelected) {
          const itemsToRemove = [...selectedGroup.inferiors, itemId];
          const filteredArray = itemsSelection.items.filter((n) => !itemsToRemove.includes(n));
          newSelection = [...filteredArray];
        } else {
          newSelection = [...itemsSelection.items, ...selectedGroup.inferiors, itemId];
        }
      } else if (selectedTask) {
        if (alreadySelected) {
          const itemsToRemove = [...selectedTask.inferiors, itemId];
          const filteredArray = itemsSelection.items.filter((n) => !itemsToRemove.includes(n));
          newSelection = [...filteredArray];
        } else {
          newSelection = [...itemsSelection.items, ...selectedTask.inferiors, itemId];
        }
      } else {
        if (alreadySelected) {
          newSelection = itemsSelection.items.filter((s) => s !== itemId);
        } else {
          newSelection = [...itemsSelection.items, itemId];
        }
      }
      onForceSelectionCallback({ items: newSelection });
    },
    [itemsSelection, onForceSelectionCallback, examTasksGroupsCriterionsIds, examTasksGroupsCriterionsIds],
  );

  const forceSelectionRef = useCallback(
    (forceSelectionCallback) => setOnForceSelectionCallback(() => forceSelectionCallback),
    [],
  );

  return (
    <div style={{ width: 'calc(100% - 30px)', margin: '10px' }}>
      {examTaskCriterionsTableData && (
        <ViewContainer>
          <div className={classes.headerBar}>
            <div className={classes.headerContainer}>
              <Typography className={classes.header}>Adnotacje</Typography>
              {itemsSelection.items.length > 0 && (
                <div className={classes.countAndButtonContainer}>
                  <div className={classes.selectedRecordCountDisplay}>
                    <SelectedRecordCountDisplay
                      selection={itemsSelection}
                      totalItems={examTaskCriterionsTableData.length}
                    />
                  </div>
                  {!isAnnotationsTableOpen && (
                    <div className={classes.assignAnnotationsButton}>
                      <AssignAdnotationsButton onClick={getAssignedAnnotationsAndOpenEditor} />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className={classes.sessionName}>{sessionNameElement}</div>
          </div>
          <span style={{ margin: isAnnotationsTableOpen ? '100px 5px 50px 5px' : '100px 5px 0 5px' }}>
            <div
              className={classes.tablesContainer}
              style={{ maxHeight: isAnnotationsTableOpen ? 'calc(100vh - 310px)' : '' }}
            >
              <div
                className={classes.table}
                style={{
                  marginLeft: '20px',
                  marginRight: isAnnotationsTableOpen ? '0px' : '20px',
                  maxHeight: isAnnotationsTableOpen ? '100%' : '',
                }}
              >
                <SelectableTable<ExamTaskCriterionsTableItem>
                  columns={columnsTaskCriterionTable}
                  rows={examTaskCriterionsTableData}
                  onSelectionChanged={setItemsSelection}
                  onItemSelectionChanged={handleItemSelectionChange}
                  forceSelectionRef={forceSelectionRef}
                  isMultiChoice={true}
                  withoutEmptyMessage={true}
                  noOverflowInitial={true}
                  oddTableDoesNotChangeColor={true}
                  isSelectionDisabled={isSelectionDisabled}
                  hideCheckboxMenu={true}
                  caption="Adnotacje"
                />
              </div>
              {isAnnotationsTableOpen && (
                <div
                  className={classes.tableLeft}
                  style={{ marginRight: '20px', maxHeight: isAnnotationsTableOpen ? '100%' : '' }}
                >
                  <AnnotationAssigmentEditor
                    selectedCriterionsIds={itemsSelection.items}
                    assignedAnnotations={assignedAnnotations}
                    annotationDefinitions={annotationDefinitions}
                    onClose={() => {
                      onForceSelectionCallback && onForceSelectionCallback({ items: [] });
                      setIsAnnotationsTableOpen(false);
                      setIsSelectionDisabled(false);
                    }}
                    onSave={getAnnotationsExamSheetStructureAssignment}
                  />
                </div>
              )}
            </div>
          </span>
        </ViewContainer>
      )}
      <Prompt
        type="info"
        open={existingAnnotationInfoModalOpen}
        mainText={`Wybrane kryterium posiada już adnotacje. Czy chcesz nadpisać nowe adnotacje do tego kryterium?`}
        mainActionButtonText="TAK"
        onConfirm={() => {
          setExistingAnnotationInfoModalOpen(false);
          setAssignedAnnotations([]);
          openEditor();
        }}
        onCancel={() => {
          setExistingAnnotationInfoModalOpen(false);
        }}
      />
    </div>
  );
};

export default Annotations;
